import { isBookableStatus, type AvailabilityStatus } from './types';

type SelectableEvent = {
  eventId: string;
  date: string;
  availability: { status: AvailabilityStatus };
};

/**
 * The id of the single bookable event in a set, when there is exactly one —
 * otherwise `null`. Lets checkout surfaces pre-select the only available time
 * so the visitor can continue without an extra click. Zero or two-plus
 * bookable events return `null` (there is no unambiguous slot to pick).
 *
 * Only a set from one date qualifies. Across several dates the sole bookable
 * event is a *different date* from whatever the visitor was looking at — e.g.
 * a deep link to a sold-out date would otherwise silently pre-select the next
 * open one, and guests book it without noticing (US-14881).
 */
export function soleBookableEventId(events: SelectableEvent[]): string | null {
  if (new Set(events.map((e) => e.date)).size > 1) return null;
  let found: string | null = null;
  for (const event of events) {
    if (!isBookableStatus(event.availability.status)) continue;
    if (found !== null) return null; // more than one bookable — pick nothing
    found = event.eventId;
  }
  return found;
}

/**
 * The event to show as selected in a day's time-slot list: `preferred` (a
 * basket selection or a deep-linked event) when that event is actually in the
 * list, otherwise the day's sole bookable slot.
 *
 * The membership check is what keeps a selection honest — a preferred id from
 * another date, or one filtered out of this list, would otherwise highlight
 * nothing while still reading as "a time is chosen", leaving Continue a dead
 * control and suppressing the sole-slot pre-selection.
 *
 * A preferred event that is present but no longer bookable stays selected: the
 * slot renders its own "not enough spots" notice, which is more use than
 * silently moving the selection.
 */
export function resolveSelectedEventId(
  events: SelectableEvent[],
  preferred: string | null
): string | null {
  if (preferred !== null && events.some((e) => e.eventId === preferred)) {
    return preferred;
  }
  return soleBookableEventId(events);
}
